import React, { useEffect, useState, useCallback } from "react";
import demoRequestsService from "../../services/demoRequests";

const STATUSES = ["All", "New", "Contacted", "Closed"];

const statusColor = {
  New: { bg: "#fdf3d6", fg: "#8a6d1b" },
  Contacted: { bg: "#e6effd", fg: "#2563c9" },
  Closed: { bg: "#e3f7e8", fg: "#1f8b3d" },
};

export default function DemoRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("All");
  // Long demo messages are clamped to a few lines; these are the rows expanded by the admin.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await demoRequestsService.getDemoRequests({
        limit: 100,
        status: filter === "All" ? undefined : filter,
      });
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || "Failed to load demo requests");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (id, status) => {
    // optimistic update
    setItems((prev) => prev.map((it) => (it._id === id ? { ...it, status } : it)));
    try {
      await demoRequestsService.updateStatus(id, status);
    } catch (err) {
      // reload on failure to resync
      load();
    }
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: "var(--text, #1c2233)", margin: 0 }}>
          Demo Requests
        </h1>
        <p style={{ color: "var(--text-muted, #8a8f9c)", marginTop: 6 }}>
          Leads submitted from the marketing site's "Request a Demo" form.
        </p>
      </div>

      {/* Status filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: "8px 18px",
              borderRadius: 999,
              border: "1px solid #e0e0e0",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 14,
              background: filter === s ? "#12162a" : "#fff",
              color: filter === s ? "#ffdb6b" : "#555",
            }}
          >
            {s}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #eee", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#8a8f9c" }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: "center", color: "#c33" }}>{error}</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#8a8f9c" }}>
            No demo requests {filter !== "All" ? `with status "${filter}"` : "yet"}.
          </div>
        ) : (
            <div className="mq-table-scroll"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 900 }}>
              <thead>
                <tr style={{ textAlign: "left", background: "#f7f5ef", color: "#555" }}>
                  <th style={th}>Date</th>
                  <th style={th}>Name</th>
                  <th style={th}>Email</th>
                  <th style={th}>Company</th>
                  <th style={th}>Team</th>
                  <th style={th}>Message</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const sc = statusColor[it.status] || statusColor.New;
                  return (
                    <tr key={it._id} style={{ borderTop: "1px solid #f0f0f0" }}>
                      <td style={td}>{fmtDate(it.createdAt)}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{it.fullName}</td>
                      <td style={td}>
                        <a href={`mailto:${it.email}`} style={{ color: "#2563c9" }}>{it.email}</a>
                      </td>
                      <td style={td}>{it.company || "—"}</td>
                      <td style={td}>{it.teamSize || "—"}</td>
                      <td style={{ ...td, width: 300, minWidth: 240, maxWidth: 340 }}>
                        {it.message ? (
                          <>
                            <div
                              title={it.message}
                              style={{
                                whiteSpace: "pre-wrap",
                                overflowWrap: "anywhere",
                                ...(expanded.has(it._id)
                                  ? {}
                                  : {
                                      display: "-webkit-box",
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }),
                              }}
                            >
                              {it.message}
                            </div>
                            {it.message.length > 120 && (
                              <button
                                type="button"
                                onClick={() => toggleExpanded(it._id)}
                                style={{
                                  marginTop: 6,
                                  padding: 0,
                                  border: "none",
                                  background: "none",
                                  color: "#2563c9",
                                  cursor: "pointer",
                                  fontSize: 13,
                                  fontWeight: 600,
                                }}
                              >
                                {expanded.has(it._id) ? "Show less" : "Show more"}
                              </button>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={td}>
                        <select
                          value={it.status}
                          onChange={(e) => changeStatus(it._id, e.target.value)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: "none",
                            fontWeight: 700,
                            cursor: "pointer",
                            background: sc.bg,
                            color: sc.fg,
                          }}
                        >
                          <option value="New">New</option>
                          <option value="Contacted">Contacted</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
        )}
      </div>
    </div>
  );
}

const th = { padding: "14px 16px", fontWeight: 600, whiteSpace: "nowrap" };
const td = { padding: "14px 16px", color: "#333", verticalAlign: "top" };
